/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  Perm Address module selectors
 */

import { getDetailHref } from '@zcode-ui/web-zumper-global-state'
import { BuildingAmenity, ListingAmenity, PropertyFeatures, getEnumTitle } from '@zcode/enums'
import { createSelector } from '@zcode/memoizers'
import { localStorageSingleton } from '@zcode/nr-utils'
import Filter from '@zcode/nr/es/shared/bizos/filter'
import { decomposeFormattedAddress } from '@zcode/nr/es/shared/utils'
import type {
  Listing,
  MinPAListingData,
  ParkingData,
  PermanentAddressMessage,
  Point,
  RoomObj,
  State,
} from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import { dollarize, isTruthy } from '@zcode/utils'
import { compose } from 'redux'
import type { Object } from 'ts-toolbelt'
import { getPAAmenities, getPAMajorityPets } from '../helpers'
import { getListingWinner } from '../helpers/getListingWinner'
export * from './constants'
export { selectListingId, type LooseListingUId } from './selectListingId'

type PAState = Pick<State, 'permanentAddress'>
type DetailEntityState = Object.P.Pick<State, ['detail', 'entity', 'data']>

export const selectPermanentAddress = (state: PAState) => state.permanentAddress

export const selectPermanentAddressCity = compose((state) => state.city, selectPermanentAddress)

export const selectPermanentAddressNeighborhood = compose(
  (state) => state.neighborhood,
  selectPermanentAddress
)

export const selectHomesListables = compose(
  (state) => state.homesListings?.listables,
  selectPermanentAddress
)

const selectHomesListing = (state: PAState, index: number) => {
  return selectHomesListables(state)?.[index]
}

export const selectHomesListingImageId = (state: PAState, index: number) => {
  return selectHomesListing(state, index)?.images?.[0]?.id
}

export const selectHomesListingCity = (state: PAState, index: number) => {
  return selectHomesListing(state, index)?.city
}

export const selectHomesListingAddress = (state: PAState, index: number) => {
  return selectHomesListing(state, index)?.address
}

export const selectHomesListingPriceText = (state: PAState, index: number) => {
  return selectHomesListing(state, index)?.priceText
}

export const selectHomesListingBedroomText = (state: PAState, index: number) => {
  return selectHomesListing(state, index)?.bedroomText
}

export const selectHomesListingUrl = (state: PAState, index: number) => {
  const homesListing = selectHomesListing(state, index)
  if (homesListing) {
    return getDetailHref(homesListing)
  }
}

const selectPetFriendlyListings = compose(
  (state) => state.petFriendlyListings,
  selectPermanentAddress
)

export const selectPetFriendlyListables = (state: PAState) => {
  return selectPetFriendlyListings(state)?.listables
}

const selectPetFriendlyListing = (state: PAState, index: number) => {
  return selectPetFriendlyListables(state)?.[index]
}

export const selectPetFriendlyListingImageId = (state: PAState, index: number) => {
  return selectPetFriendlyListing(state, index)?.images?.[0]?.id
}

export const selectPetFriendlyListingCity = (state: PAState, index: number) => {
  return selectPetFriendlyListing(state, index)?.city
}

export const selectPetFriendlyListingAddress = (state: PAState, index: number) => {
  return selectPetFriendlyListing(state, index)?.address
}

export const selectPetFriendlyListingPriceText = (state: PAState, index: number) => {
  return selectPetFriendlyListing(state, index)?.priceText
}

export const selectPetFriendlyListingUrl = (state: PAState, index: number) => {
  const petFriendlyListing = selectPetFriendlyListing(state, index)
  if (petFriendlyListing) {
    return getDetailHref(petFriendlyListing)
  }
}

export const selectActiveListings = compose(
  (state): NonNullable<(typeof state)['activeListings']> => state?.activeListings ?? [],
  selectPermanentAddress
)

export const selectAddress = compose(
  (state): MaybeEmpty<(typeof state)['address']> => state?.address ?? {},
  selectPermanentAddress
)

const selectFormattedAddress = compose((state) => state.formattedAddress, selectAddress)

export const selectDecomposedAddress = compose(decomposeFormattedAddress, selectFormattedAddress)

export const selectShortAddress = compose(
  ({ shortAddress }) => shortAddress,
  selectDecomposedAddress
)

export const selectFormattedAddressWithoutZip = compose((state) => {
  return (state && /^(.*?) \d\d\d\d\d, /.exec(state)?.[1]) ?? state
}, selectFormattedAddress)

export const selectDILAddresses = compose(
  (state): NonNullable<(typeof state)['dilAddresses']> => state.dilAddresses ?? [],
  selectPermanentAddress
)

export const selectDILBuildings = compose(
  (state): NonNullable<(typeof state)['dilBuildings']> => state.dilBuildings ?? [],
  selectPermanentAddress
)

export const selectForbiddenListingIds = compose(
  (state) => state.forbiddenListingIds,
  selectPermanentAddress
)

export const selectInactiveListings = compose(
  (state): NonNullable<(typeof state)['inactiveListings']> => state?.inactiveListings ?? [],
  selectPermanentAddress
)

export const selectPriceData = compose((state) => state.priceData, selectPermanentAddress)

export const selectHoodPriceData = compose((state) => state.hoodPriceData, selectPermanentAddress)

const getRecent = (series?: readonly Point[]) => series && series[series.length - 1]

// given a series, return the most recent price (y value)
const getRecentPrice = (series?: readonly Point[]) => {
  const recent = getRecent(series)
  return recent?.y ?? null
}

const selectRecentPrice = (state: PAState, { room }: RoomObj) => {
  const cityData = selectPriceData(state)
  const hoodData = selectHoodPriceData(state)
  const citySeries = cityData?.[room]?.series
  const cityPrice = getRecentPrice(citySeries)
  const hoodSeries = hoodData?.[room]?.series
  const hoodPrice = getRecentPrice(hoodSeries)
  return hoodData?.length ? dollarize(hoodPrice) : dollarize(cityPrice)
}

export const selectAddressRecentPrices = (state: PAState) => {
  const studioPrice = selectRecentPrice(state, { room: 0 })
  const oneBedPrice = selectRecentPrice(state, { room: 1 })
  const twoBedPrice = selectRecentPrice(state, { room: 2 })
  const threeBedPrice = selectRecentPrice(state, { room: 3 })
  const fourBedPrice = selectRecentPrice(state, { room: 4 })
  return [studioPrice, oneBedPrice, twoBedPrice, threeBedPrice, fourBedPrice]
}

export const selectAddressNeighborhoodName = compose(
  (state) => state?.name,
  selectPermanentAddressNeighborhood
)

export const selectAddressNeighborhoodCityStateName = compose(
  (state) => state?.cityStateName,
  selectPermanentAddressNeighborhood
)

export const selectAddressNeighborhoodCity = compose(
  (state) => state?.city,
  selectPermanentAddressNeighborhood
)

export const selectAddressNeighborhoodState = compose(
  (state) => state?.state,
  selectPermanentAddressNeighborhood
)

const selectWalkscore = (state: State) => state.walkscore.data
export const selectWalkscoreScore = (state: State) => selectWalkscore(state)?.walkscore
export const selectWalkscoreDescription = (state: State) => selectWalkscore(state)?.description

export const selectPoi = (state: State) => state.poi.data

const selectRentHistoryDetailWithMeta = compose(
  (permanentAddress) => permanentAddress.rentHistoryDetail,
  selectPermanentAddress
)

const selectRentHistoryDetail = compose(
  (rentHistoryDetail) => rentHistoryDetail.data,
  selectRentHistoryDetailWithMeta
)

export const selectRentHistoryChanges = compose(
  (data) => data?.rentHistory,
  selectRentHistoryDetail
)

const selectRentHistoryDetailMeta = compose(
  (rentHistoryDetail) => rentHistoryDetail.meta,
  selectRentHistoryDetailWithMeta
)

export const selectRentHistoryDetailCurrentPaUrl = compose(
  (meta) => meta.currentPaUrl,
  selectRentHistoryDetailMeta
)

export const selectRentHistoryDetailFetchingPaUrl = compose(
  (meta) => meta.fetchingPaUrl,
  selectRentHistoryDetailMeta
)

export const selectRentHistorySummary = compose(
  (state) => state?.rentHistorySummary,
  selectPermanentAddress
)

export const selectPetPolicy = compose((state) => state?.petPolicy, selectPermanentAddress)

export const selectAllListings = (state: PAState) => {
  const activeListings = selectActiveListings(state)
  const inactiveListings = selectInactiveListings(state)
  return activeListings && inactiveListings
    ? activeListings.concat(inactiveListings)
    : (activeListings ?? inactiveListings)
}

export const selectIsAddressInactive = (state: PAState) => {
  const allListings = selectAllListings(state)
  const inactiveListings = selectInactiveListings(state)

  return inactiveListings.length === allListings.length
}

/**
 * Checks the `pets` field of each listing:
 *
 * - If >= 50% of the listings allow cats (or dogs), return catsOk: true (or dogsOk: true)
 * - If >= 50% of the listings disallow cats (or dogs), return catsOk: false (or dogsOk: false)
 * - If neither of the above, return catsOk: null (or dogsOk: null)
 */
export const selectMajorityPets = compose(getPAMajorityPets, selectAllListings)

export const selectParkingData = createSelector(
  selectAllListings,
  (allListings): readonly ParkingData[] => {
    const { buildingAmenities, otherAmenities } = getPAAmenities(allListings)
    return [
      buildingAmenities.includes(getEnumTitle(BuildingAmenity, BuildingAmenity.GARAGE_PARKING)) && {
        type: 'Garage Parking',
      },
      otherAmenities.includes(getEnumTitle(ListingAmenity, ListingAmenity.ASSIGNED_PARKING)) && {
        type: 'Assigned Parking',
      },
      otherAmenities.includes(getEnumTitle(ListingAmenity, ListingAmenity.FREE_PARKING)) && {
        type: 'Free Parking',
      },
    ].filter(isTruthy)
  }
)

export const selectHasShow0ForFees = createSelector(selectAllListings, (allListings) => {
  return !!allListings.some((listing) =>
    listing?.features?.includes(PropertyFeatures.SHOW_0_FOR_FEES)
  )
})

export const selectDetailEntity = (state: DetailEntityState) => state.detail?.entity.data

export const selectIsSingleUnit = (state: DetailEntityState & PAState) => {
  const detailEntity = selectDetailEntity(state)
  const listingCount = selectAllListings(state).length
  return !!detailEntity && listingCount === 1
}

export const selectIsOnlyActiveUnitOfMultiUnit = (state: DetailEntityState & PAState) => {
  const detailEntity = selectDetailEntity(state)
  const hasOneActiveUnit = selectActiveListings(state).length === 1
  const hasInactiveUnits = !!selectInactiveListings(state).length
  return !!detailEntity && hasOneActiveUnit && hasInactiveUnits
}

export const selectIsMultiUnit = (state: DetailEntityState & PAState) => {
  const detailEntity = selectDetailEntity(state)
  const hasMultipleActiveUnits = selectActiveListings(state).length > 1
  return !!detailEntity && hasMultipleActiveUnits
}

export const selectActiveMessageableListings = compose((listings) => {
  return listings.filter((l) => l.isMessageable)
}, selectActiveListings)

const selectMessage = compose(
  (permAddress) =>
    ((permAddress?.message as PermanentAddressMessage) ?? permAddress?.message) || {},
  selectPermanentAddress
)

export const selectMessageListingDetails = compose(
  (message) => message.selectedListingDetails,
  selectMessage
)

export const selectListingDetailsError = compose((message) => message?.meta?.error, selectMessage)

export const selectTourEntity = (state: DetailEntityState & PAState) => {
  return selectDetailEntity(state) ?? selectMessageListingDetails(state)
}

export const selectRelatedPropertiesEntity = compose((listings) => {
  return getListingWinner(listings)
}, selectAllListings)

export const selectEntityGroupId = compose(
  (entity) => entity?.listingId,
  selectRelatedPropertiesEntity
)

export const selectFirstPermanentAddressListing = (state: PAState) => {
  return state.permanentAddress?.activeListings?.length
    ? state.permanentAddress?.activeListings?.[0]
    : state.permanentAddress?.inactiveListings?.[0]
}

export const selectListingIdFromFilters = compose((activeListings) => {
  let listingId = ''
  if (activeListings.length === 1) {
    listingId = activeListings[0].uId ?? ''
  } else if (activeListings.length > 1) {
    const bedrooms = Filter.fromJS(localStorageSingleton.get('prevSearchFilters') || {}).bedrooms
    let bestListing: Listing<MinPAListingData> | undefined
    for (const listing of activeListings) {
      if (bedrooms[listing.bedrooms] && (bestListing?.promotion ?? 0) <= (listing.promotion ?? 0)) {
        bestListing = listing
      }
    }
    listingId = bestListing?.listingId ? String(bestListing?.listingId) : ''
  }
  return listingId.replace('l-', '')
}, selectActiveListings)

export const selectDetailCityState = compose(
  (detailEntity) => detailEntity?.cityState,
  selectDetailEntity
)
export const selectNeighborhoodPath = compose(
  (detailEntity) => detailEntity?.neighborhood?.path,
  selectDetailEntity
)

export const selectDetailHoodName = compose(
  (detailEntity) => detailEntity?.neighborhood?.name,
  selectDetailEntity
)
